import type { CatalogueCategory, MockCatalogueItem } from "@/data/mockCatalogueItems";
import { resolveProductDisplayName } from "@shared/productDisplayName";
import { isClarityPlaceholderDocumentUrl } from "@/lib/catalogueGoogleSearch";
import {
  normaliseCatalogueDescriptionSource,
  resolveEffectiveCatalogueDescription,
} from "@shared/catalogueDualDescription";

/** TRPC catalogue row (full or lightweight projection). */
export type ApiCatalogueItemRow = {
  id: number;
  brand: string;
  model: string;
  name: string;
  displayName?: string | null;
  displayNameManuallyEdited?: boolean | null;
  description?: string | null;
  descriptionPreview?: string | null;
  originalDescription?: string | null;
  aiGeneratedDescription?: string | null;
  selectedDescriptionSource?: "original" | "ai" | string | null;
  descriptionStatus?: "pending" | "generated" | "failed" | "manual" | null;
  finish: string | null;
  unit: string | null;
  category: string;
  price: string;
  currency: string | null;
  sku: string | null;
  imageUrl: string | null;
  showOnWebsite?: boolean;
  hideFromWebsite?: boolean;
  inResidence?: boolean;
  officialProductUrl?: string | null;
  productFamily?: string | null;
  importListPosition?: number;
  brochureUrl: string | null;
  specsUrl: string | null;
  reviewsUrl: string | null;
  isActive: boolean;
  isCustom?: boolean;
  approvalStatus?: "pending" | "approved" | null;
  createdByUserId?: number | null;
  pricingMethod?: string | null;
  defaultPercentage?: string | number | null;
  configuratorType?: string | null;
  selfQuoteCount?: number | null;
  teamQuoteCount?: number | null;
  selfLastQuotedAt?: string | Date | null;
  teamLastQuotedAt?: string | Date | null;
};

export type DescriptionStatus = "pending" | "generated" | "failed" | "manual";

/** One line for list/detail views; keeps empty string when there is no catalogue blurb (even if brand/model exist). */
export function getCatalogueDescriptionLine(
  item: MockCatalogueItem
): { text: string; isPlaceholder: boolean } {
  const t = item.shortDescription?.trim() ?? "";
  if (t) return { text: t, isPlaceholder: false };
  if (item.source === "live") {
    if (item.descriptionStatus === "pending") {
      return { text: "No description yet — auto-generation in progress or queued.", isPlaceholder: true };
    }
    if (item.descriptionStatus === "failed") {
      return { text: "Description generation failed. Ask an admin to retry or add text in the file.", isPlaceholder: true };
    }
  }
  return { text: "—", isPlaceholder: true };
}

function mapDbCategoryToUiCategory(cat: string): CatalogueCategory {
  const t = cat.toLowerCase();
  if (t.includes("personal audio") || t.includes("earbud") || t.includes("headphone") || t.includes("portable")) {
    return "Headphones";
  }
  if (
    t.includes("private cinema") ||
    t.includes("cinema") ||
    t.includes("projector") ||
    t.includes("subwoofer") ||
    t.includes("screen")
  ) {
    return "Home Cinema";
  }
  if (t.includes("2-channel") || t.includes("amplifier") || t.includes("dac") || t.includes("streamer") || t.includes("speaker")) {
    return "Hi-Fi";
  }
  if (t.includes("automation") || t.includes("lighting") || t.includes("control")) {
    return "Accessories";
  }
  return "Accessories";
}

function buildUnitLabelFromUom(uom: string | null | undefined, displayName: string): string {
  const raw = (uom ?? "")
    .replace(/^uom\s*:\s*/i, "")
    .replace(/^unit\s*:\s*/i, "")
    .trim();
  const u = raw.toLowerCase();
  if (raw) {
    if (u === "pair" || u === "pr" || u === "pairs") return "/ pair";
    if (u === "each" || u === "ea" || u === "pc" || u === "unit" || u === "nos" || u === "no" || u === "no.") return "/ each";
    if (u === "m" || u === "metre" || u === "meter" || u === "metres") return "/ m";
    return `/ ${raw}`;
  }
  if (/\(pair\)|\bpair\b|set\/4|×\s*4|four\b/i.test(displayName)) return "/ pair";
  return "/ each";
}

function cleanMetaValue(v: string | null | undefined): string {
  return (v ?? "")
    .replace(/^finish\s*:\s*/i, "")
    .replace(/^uom\s*:\s*/i, "")
    .replace(/^unit\s*:\s*/i, "")
    .trim();
}

/**
 * Map a live DB row to the same `MockCatalogueItem` shape the Sales UI already renders.
 */
export function mapApiCatalogueItemToView(row: ApiCatalogueItemRow): MockCatalogueItem {
  const id = String(row.id);
  const price = Number(row.price);
  const displayName = resolveProductDisplayName(
    {
      brand: row.brand,
      model: row.model,
      name: row.name,
      displayName: row.displayName,
    },
    { preferNameOverBrandModel: !row.displayName?.trim() },
  );
  const unitLabel = buildUnitLabelFromUom(row.unit, displayName);
  const selectedSource = normaliseCatalogueDescriptionSource(row.selectedDescriptionSource);
  const rawDesc = resolveEffectiveCatalogueDescription({
    description: row.description,
    originalDescription: row.originalDescription,
    aiGeneratedDescription: row.aiGeneratedDescription,
    selectedDescriptionSource: selectedSource,
  });
  const descriptionPreview = (row.descriptionPreview ?? "").trim();
  const effectiveDescription = rawDesc?.trim() || descriptionPreview;
  const st = (row.descriptionStatus ?? null) as DescriptionStatus | null;
  const finish = cleanMetaValue(row.finish);
  const uom = cleanMetaValue(row.unit);
  const specLine = [finish ? `Finish: ${finish}` : "", uom ? `UOM: ${uom}` : ""].filter(Boolean).join(" · ") || "—";
  const specs: Record<string, string> = {};
  const sku = row.sku?.trim() || `LIVE-${row.id}`;

  const isCustom = Boolean(row.isCustom);
  const approvalStatus = (row.approvalStatus ?? "approved") as "pending" | "approved";
  return {
    id,
    catalogueItemId: row.id,
    source: isCustom ? "user_added" : "live",
    sku,
    brand: row.brand,
    model: row.model,
    displayName,
    category: mapDbCategoryToUiCategory(row.category),
    productType: row.category.split("/")[0]?.trim() || "Product",
    retailPrice: Number.isFinite(price) ? price : 0,
    currency: "SGD",
    isActive: row.isActive,
    shortDescription: effectiveDescription,
    descriptionStatus: st,
    originalDescription: row.originalDescription?.trim() || null,
    aiGeneratedDescription: row.aiGeneratedDescription?.trim() || null,
    selectedDescriptionSource: selectedSource,
    specs,
    tags: [],
    specLine,
    imageUrl: row.imageUrl?.trim() || null,
    showOnWebsite: Boolean(row.showOnWebsite),
    hideFromWebsite: Boolean(row.hideFromWebsite),
    inResidence: Boolean(row.inResidence),
    officialProductUrl: row.officialProductUrl?.trim() || null,
    productFamily: row.productFamily?.trim() || null,
    importListPosition: Number(row.importListPosition ?? 0),
    unitLabel,
    brochureUrl:
      row.brochureUrl?.trim() && !isClarityPlaceholderDocumentUrl(row.brochureUrl) ? row.brochureUrl.trim() : "",
    specSheetUrl:
      row.specsUrl?.trim() && !isClarityPlaceholderDocumentUrl(row.specsUrl) ? row.specsUrl.trim() : "",
    reviewsUrl:
      row.reviewsUrl?.trim() && !isClarityPlaceholderDocumentUrl(row.reviewsUrl) ? row.reviewsUrl.trim() : "",
    reviewStatus: approvalStatus === "pending" ? "pending_review" : "approved",
    addedBy: row.createdByUserId != null ? String(row.createdByUserId) : null,
    addedAt: null,
    linkedProposalId: null,
    quoteCount: Number(row.selfQuoteCount ?? row.teamQuoteCount ?? 0),
    winCount: Number(row.teamQuoteCount ?? 0),
    lastQuotedAt:
      (typeof row.selfLastQuotedAt === "string" ? row.selfLastQuotedAt : row.selfLastQuotedAt?.toISOString?.()) ??
      (typeof row.teamLastQuotedAt === "string" ? row.teamLastQuotedAt : row.teamLastQuotedAt?.toISOString?.()) ??
      null,
    finish: finish || null,
    uom: uom || null,
    pricingMethod:
      String(row.pricingMethod ?? "").toLowerCase() === "percentage_of_equipment"
        ? "percentage_of_equipment"
        : "fixed",
    defaultPercentage:
      row.defaultPercentage != null && Number.isFinite(Number(row.defaultPercentage))
        ? Number(row.defaultPercentage)
        : null,
    configuratorType: row.configuratorType ?? null,
  };
}
